import {
  View,
  Text,
  Modal,
  TextInput,
  StyleSheet,
  Platform,
  Keyboard,
  Dimensions,
  useWindowDimensions,
} from 'react-native';
import { WEB_MODAL_MAX_WIDTH } from '../../theme/webLayoutWidths';
import React, { useState, useEffect, useRef } from 'react';
import { theme, PoppinsFonts } from '../../theme';
import Button from '../Reusables/Button';
import {
  fs,
  s,
  v,
  borderWidth,
  roundMoney,
  sanitizeMoneyInput,
} from '../../utils/libs';
import Typography from '../Reusables/Typography';
import KeyboardAwareFormScroll from '../Reusables/KeyboardAwareFormScroll';
import { shouldUseWidthConstraint } from '../../utils/layoutUtils';
import { getProductUnitAffixText } from '../../screens/products/newproducts/components/ProductUnitAffix';

const isWeb = Platform.OS === 'web';

interface PriceEditModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (newPrice: number) => void;
  currentPrice: number;
  itemName: string;
  priceType?: string;
  unitOfMass?: string;
}

const PriceEditModal = ({
  visible,
  onClose,
  onSave,
  currentPrice,
  itemName,
  priceType = 'item',
  unitOfMass = '',
}: PriceEditModalProps) => {
  const { width, height } = useWindowDimensions();
  const constrainWidth = shouldUseWidthConstraint(width, height);
  const [priceInput, setPriceInput] = useState('');
  const inputRef = useRef<TextInput>(null);
  const unitAffix = getProductUnitAffixText({
    priceType,
    unitOfMass,
    kind: 'price',
  });

  useEffect(() => {
    if (visible) {
      setPriceInput(String(roundMoney(currentPrice)));
      // Focus immediately using requestAnimationFrame for fastest possible focus
      // This runs on the next frame without blocking the UI thread
      const frameId = requestAnimationFrame(() => {
        // Small delay to ensure TextInput is mounted, but still very fast
        setTimeout(() => {
          inputRef.current?.focus();
        }, 30);
      });
      return () => cancelAnimationFrame(frameId);
    } else {
      // Dismiss keyboard when modal closes
      Keyboard.dismiss();
    }
  }, [visible, currentPrice]);

  const handleSave = () => {
    const price = parseFloat(priceInput);
    if (!isNaN(price) && price >= 0) {
      // Unchanged: keep the line's exact rate (a bargained rate can carry
      // more than two decimals, e.g. 1000 ÷ 3) instead of re-saving 2dp.
      if (price !== roundMoney(currentPrice)) onSave(price);
      onClose();
    }
  };

  const isLargeScreen = Dimensions.get('window').width >= 1024;
  const isMediumScreen = Dimensions.get('window').width >= 768;

  return (
    <Modal
      animationType="fade"
      visible={visible}
      statusBarTranslucent={true}
      navigationBarTranslucent={true}
      transparent={true}
      onRequestClose={onClose}>
      <KeyboardAwareFormScroll
        contentContainerStyle={styles.popupOverlay}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        baseBottomPadding={v(20)}
        bottomOffset={v(20)}>
        <View
          style={[
            styles.popupContainer,
            constrainWidth
              ? {
                  maxWidth: WEB_MODAL_MAX_WIDTH,
                  width: '90%',
                  alignSelf: 'center',
                }
              : { width: '80%' },
            {
              maxHeight: v(isLargeScreen ? 360 : isMediumScreen ? 300 : 240),
            },
          ]}>
          <View style={{ flex: 1 }}>
            <Typography style={styles.popupTitle}>
              Edit Selling Price
            </Typography>
            <Text style={styles.itemName}>
              Sell {itemName} at a premium or discount
            </Text>

            <View style={styles.inputRow}>
              <TextInput
                ref={inputRef}
                value={priceInput}
                onChangeText={t => setPriceInput(sanitizeMoneyInput(t))}
                placeholder="Enter new price"
                placeholderTextColor="#9CA3AF"
                keyboardType="decimal-pad"
                style={[styles.input, unitAffix ? styles.inputWithAffix : null]}
                autoFocus={true}
                selectTextOnFocus={true}
              />
              {unitAffix ? (
                <Text style={styles.unitAffix}>{unitAffix}</Text>
              ) : null}
            </View>
          </View>

          <View style={styles.modalBtnRow}>
            <View style={{ flex: 1 }}>
              <Button
                text="Cancel"
                onPress={onClose}
                backgroundColor={theme.colors.errorDark}
                borderRadius={s(5)}
                height={v(40)}
                fontSize={fs(14)}
              />
            </View>
            <View style={{ flex: 1 }}>
              <Button
                text="Update"
                onPress={handleSave}
                disabled={
                  !priceInput ||
                  Number(priceInput) < 0 ||
                  isNaN(parseFloat(priceInput))
                }
                backgroundColor={theme.colors.success}
                borderRadius={s(5)}
                height={v(40)}
                fontSize={fs(14)}
              />
            </View>
          </View>
        </View>
      </KeyboardAwareFormScroll>
    </Modal>
  );
};

const styles = StyleSheet.create({
  popupOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: isWeb ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.5)',
  },
  popupContainer: {
    flex: 1,
    backgroundColor: 'white',
    borderRadius: s(8),
    padding: s(20),
    ...(isWeb && {
      maxWidth: WEB_MODAL_MAX_WIDTH,
    }),
  },
  popupTitle: {
    fontSize: fs(18),
    fontWeight: 'bold',
    marginBottom: v(10),
    textAlign: 'center',
  },
  itemName: {
    fontSize: fs(14),
    color: theme.colors.textSecondary,
    marginBottom: v(15),
    textAlign: 'center',
    fontFamily: PoppinsFonts.regular,
  },
  inputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: v(20),
    borderWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
    borderColor: theme.colors.borderGray,
    borderRadius: s(8),
    backgroundColor: '#FFFFFF',
    minHeight: v(50),
    paddingHorizontal: s(12),
  },
  input: {
    flex: 1,
    height: v(50),
    paddingHorizontal: s(4),
    fontSize: fs(18),
    lineHeight: fs(20),
    color: '#333',
    fontFamily: PoppinsFonts.semibold,
    textAlign: 'center',
  },
  inputWithAffix: {
    textAlign: 'right',
    paddingRight: s(4),
  },
  unitAffix: {
    fontSize: fs(16),
    lineHeight: fs(18),
    color: theme.colors.textSecondary,
    fontFamily: PoppinsFonts.semibold,
    marginLeft: s(4),
  },
  modalBtnRow: {
    flexDirection: 'row',
    gap: s(10),
  },
});

export default PriceEditModal;
